/* `fetch_from`: the fields that fill themselves in from a linked record.
 *
 * ERPNext declares them as `customer.customer_name` — "when `customer` is
 * chosen, copy its `customer_name` here". The server applies them on save, so
 * the value is always right in the end; what is missing without this is the
 * *feedback*: you pick a customer and the address, tax id and currency sit
 * blank until you save, which reads as a form that is not working.
 *
 * Only fields that are actually empty are filled, unless the DocType says
 * otherwise — overwriting something a person typed on purpose is worse than
 * leaving a blank.
 */
import type { DocField, Doc } from './types'

export type Fetch = { field: string; from: string; source: string; onlyIfEmpty: boolean }

/** Which fields depend on `linkField`, and where each takes its value from. */
export function fetchesFor(fields: DocField[], linkField: string): Fetch[] {
  const out: Fetch[] = []
  for (const f of fields) {
    const spec = f.fetch_from
    if (!spec) continue
    const dot = spec.indexOf('.')
    if (dot < 1) continue
    if (spec.slice(0, dot) !== linkField) continue
    out.push({
      field: f.fieldname,
      from: linkField,
      source: spec.slice(dot + 1),
      // `fetch_if_empty` means "only when blank". Without it ERPNext
      // overwrites, which is what the server does on save too.
      onlyIfEmpty: Boolean(f.fetch_if_empty),
    })
  }
  return out
}

/** The values to apply, given the linked document. */
export function applyFetches(fetches: Fetch[], linked: Doc, current: Doc): Record<string, unknown> {
  const changes: Record<string, unknown> = {}
  for (const f of fetches) {
    const value = linked[f.source]
    if (value === undefined) continue
    const now = current[f.field]
    if (f.onlyIfEmpty && now !== undefined && now !== null && now !== '') continue
    if (now === value) continue
    changes[f.field] = value
  }
  return changes
}

/** Every DocType a form might need to read to fill its fetch_from fields. */
export function linkFieldsWithFetches(fields: DocField[]): string[] {
  const links = new Set<string>()
  for (const f of fields) {
    const dot = f.fetch_from?.indexOf('.') ?? -1
    if (dot > 0) links.add(f.fetch_from!.slice(0, dot))
  }
  return [...links]
}
